import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import api, { isDefinitiveRefreshRejection, refreshSession } from '../services/api';
import { resolveAssetUrl } from '../utils/assets';
import { initPushNotifications, deregisterPushNotifications } from '../services/push-notifications';
import {
  can as rbacCan,
  canAll as rbacCanAll,
  canAny as rbacCanAny,
  hasFullAccess,
  isLegacySnapshot,
  meets as rbacMeets,
  scopeOf as rbacScopeOf,
  snapshotFromUser,
} from '../rbac/can';
import { canOnRecord as rbacCanOnRecord, type RecordRelation } from '../rbac/record';
import { landingRouteName } from '../rbac/navigation';
import { isScope } from '../rbac/scopes';
import { isPermissionSlug } from '../rbac/slugs';
import type { PermissionRequirement, PermissionSlug, PermissionSnapshot, Principal, Scope } from '../rbac/types';

/**
 * The role's slug as the API sends it. Display only: access decisions use
 * permissions and the principal, never this value (a custom role has any slug).
 */
export type UserRoleSlug = 'superadmin' | 'admin' | 'employee' | 'client' | (string & {});

export interface User {
  id: number;
  email: string;
  pendingEmail?: string | null;
  firstName: string;
  lastName: string;
  avatar?: string | null;
  phone?: string | null;
  locale?: string;
  timezone?: string;
  about?: string | null;
  isOwner?: boolean;
  createdAt?: string;
  role: UserRoleSlug;
  roleId?: number | null;
  roleName?: string | null;
  companyId?: number;
  company?: {
    id: number;
    name: string;
    subdomain: string;
    logo?: string | null;
    locale?: string;
    country?: string;
  };
  /** Effective permission slugs (owner / full access = every entitled slug). */
  permissions?: string[];
  /** slug → scope; undefined on sessions cached before the RBAC backend (legacy). */
  permissionScopes?: Partial<Record<string, Scope>>;
  /** Same value as the X-Permissions-Version response header. */
  permissionsVersion?: string | null;
  /** Owner or a full-access role. */
  fullAccess?: boolean;
  enabledModules?: string[];
  isTrialing?: boolean;
  trialDaysRemaining?: number;
  subscriptionStatus?: 'trialing' | 'active' | 'past_due' | 'expired';
  billingSetupCompleted?: boolean;
  isDemo?: boolean;
  designation?: string;
  department?: string;
  reportingTo?: string;
  employeeId?: number;
  employeeCode?: string;
  emailVerified?: boolean;
  telegramChatId?: string | null;
  whatsappNumber?: string | null;
  employee?: {
    id: number;
    code: string;
    departmentId?: number;
    departmentName?: string;
    designationId?: number;
    designationName?: string;
    joiningDate?: string;
    employmentType?: string;
    reportingTo?: {
      id: number;
      name: string;
      avatar?: string;
    };
  };
}

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
  twoFactorCode?: string;
  turnstileToken?: string;
}

export interface LoginResult {
  success: boolean;
  twoFactorRequired?: boolean;
  userId?: number;
}

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  companyName: string;
  subdomain: string;
  turnstileToken?: string;
}

// Persisted-session key. We keep the last-known user object in localStorage
// so the installed PWA can launch offline: on a network failure during the
// /auth/me check we restore this instead of bouncing the user to /signin.
// Cleared on logout and on a hard 401, so a deauthorized session can't be
// resurrected offline.
const USER_STORAGE_KEY = 'tasklic_user';

function readPersistedUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function writePersistedUser(u: User | null): void {
  try {
    if (u) localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_STORAGE_KEY);
  } catch {
    /* storage full / disabled — non-fatal, offline restore just won't work */
  }
}

/**
 * True when the request never reached a responding server (offline, DNS
 * failure, timeout). Axios only populates `error.response` when the server
 * actually replied — a 401/403/500 HAS a response, a dropped connection
 * does not. We use this to keep the user signed in offline while still
 * logging them out on a genuine auth rejection.
 */
function isOfflineError(err: unknown): boolean {
  const e = err as { response?: unknown } | undefined;
  return !!err && !e?.response;
}

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | undefined)?.response?.status;
}

/**
 * The server was unreachable or failing rather than refusing the session:
 * offline, a timeout, 408, 429 or 5xx (including a refresh that failed that
 * way). The session is kept; only an authentication verdict ends it.
 */
function isTransientError(err: unknown): boolean {
  if (isOfflineError(err)) return true;
  const status = httpStatus(err);
  return status === 408 || status === 429 || (status !== undefined && status >= 500);
}

/**
 * services/api.ts announces every token refresh it makes (401 interceptor,
 * socket re-authentication) with this window event. Same value as its
 * TOKENS_REFRESHED_EVENT; not imported, because most suites mock that module.
 */
const TOKENS_REFRESHED_EVENT = 'tasklic:tokens-refreshed';

/** Session type → principal. Never derived from the role slug. */
function principalOf(userType: string | null): Principal | null {
  switch (userType) {
    case 'user':
      return 'staff';
    case 'client':
      return 'client';
    case 'superadmin':
      return 'superadmin';
    default:
      return null;
  }
}

/**
 * Profile fields the profile endpoints may change in the stored user (R9).
 * Permissions, scopes, version, role, owner flag and company id are NOT
 * here: GET/PATCH /auth/profile don't carry them (and used to hard-code the
 * role), so a profile save must never touch them.
 */
const PROFILE_FIELDS = [
  'email', 'pendingEmail', 'firstName', 'lastName', 'avatar', 'phone', 'locale', 'timezone', 'about',
  'telegramChatId', 'whatsappNumber', 'designation', 'department', 'reportingTo', 'employeeId',
  'employeeCode', 'employee', 'createdAt',
] as const;
const COMPANY_DISPLAY_FIELDS = ['name', 'logo', 'locale', 'country'] as const;

/** Fields a permission refresh may change (compared to report "changed"). */
const PERMISSION_FIELDS = ['permissions', 'permissionScopes', 'permissionsVersion', 'fullAccess', 'isOwner', 'enabledModules', 'role', 'roleId', 'roleName'] as const;

function sanitizeScopes(raw: unknown): Partial<Record<string, Scope>> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Partial<Record<string, Scope>> = {};
  for (const [slug, scope] of Object.entries(raw as Record<string, unknown>)) {
    if (isScope(scope)) out[slug] = scope;
  }
  return out;
}

function stableKey(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableKey).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value as object).sort().map((k) => `${k}:${stableKey((value as Record<string, unknown>)[k])}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'undefined';
}

export const useAuthStore = defineStore('auth', () => {
  // Seed from the persisted session so the shell can render immediately
  // (offline cold start) before checkAuth() resolves.
  const user = ref<User | null>(readPersistedUser());
  const accessToken = ref<string | null>(localStorage.getItem('accessToken'));
  const refreshToken = ref<string | null>(localStorage.getItem('refreshToken'));
  const userType = ref<string | null>(localStorage.getItem('userType')); // 'superadmin' | 'user' | 'client'
  const isInitialized = ref(false);
  const loading = ref(false);

  // Single source of truth for persistence: whenever the user object
  // changes (login, checkAuth, refresh, profile edit, logout=null) mirror
  // it to localStorage. deep so nested company/profile edits persist too.
  watch(user, (u) => writePersistedUser(u), { deep: true });

  // A refresh made by the API layer updates this store too, not only
  // localStorage: the socket authenticates with `accessToken` from here, and
  // was left re-authenticating with the expired one after a reconnect.
  if (typeof window !== 'undefined') {
    window.addEventListener(TOKENS_REFRESHED_EVENT, (event) => {
      const detail = (event as CustomEvent<{ accessToken?: unknown; refreshToken?: unknown }>).detail;
      if (typeof detail?.accessToken === 'string' && detail.accessToken) accessToken.value = detail.accessToken;
      if (typeof detail?.refreshToken === 'string' && detail.refreshToken) refreshToken.value = detail.refreshToken;
    });
  }

  const isAuthenticated = computed(() => !!user.value && !!accessToken.value);

  const fullName = computed(() => {
    if (!user.value) return '';
    return `${user.value.firstName} ${user.value.lastName}`;
  });

  const avatarUrl = computed(() => {
    return resolveAssetUrl(user.value?.avatar);
  });

  // ---------- permissions (rbac/) ----------

  /** staff | client | superadmin — from the session type, never from the role (R1). */
  const principal = computed<Principal | null>(() => (user.value ? principalOf(userType.value) : null));

  /** What the pure rbac helpers read. Recomputed whenever the user object is replaced. */
  const permissionSnapshot = computed<PermissionSnapshot>(() => snapshotFromUser(principal.value, user.value));

  /** A staff session cached before the server sent permissionScopes. */
  const hasLegacyPermissions = computed(() => !!user.value && isLegacySnapshot(permissionSnapshot.value));
  const isFullAccess = computed(() => hasFullAccess(permissionSnapshot.value));
  const permissionsVersion = computed(() => user.value?.permissionsVersion ?? null);
  /** When the permissions were last confirmed by the server (ms epoch). */
  const lastPermissionsCheckAt = ref<number | null>(null);

  /** Landing page NAME: first permitted page, else Profile (staff); the principal's dashboard otherwise. */
  const defaultRoute = computed(() => (user.value ? landingRouteName(permissionSnapshot.value) : 'login'));

  /** Staff permission check. Fail closed; no owner bypass once scopes are known. */
  function can(slug: PermissionSlug, minScope?: Scope): boolean {
    return rbacCan(permissionSnapshot.value, slug, minScope);
  }
  function canAny(slugs: readonly PermissionSlug[], minScope?: Scope): boolean {
    return rbacCanAny(permissionSnapshot.value, slugs, minScope);
  }
  function canAll(slugs: readonly PermissionSlug[], minScope?: Scope): boolean {
    return rbacCanAll(permissionSnapshot.value, slugs, minScope);
  }
  function scopeOf(slug: PermissionSlug): Scope | null {
    return rbacScopeOf(permissionSnapshot.value, slug);
  }
  /** Route / nav / v-can requirement: slug | {anyOf} | {allOf} | {slug, scope} | slug[] (anyOf). */
  function meets(req: PermissionRequirement | readonly PermissionSlug[] | null | undefined): boolean {
    return rbacMeets(permissionSnapshot.value, req);
  }
  /** Per-row check: is this record inside my scope for `slug`? */
  function canOnRecord(slug: PermissionSlug, record: RecordRelation): boolean {
    return rbacCanOnRecord(permissionSnapshot.value, user.value?.id ?? null, slug, record);
  }

  /**
   * Kept for existing call sites (P7 migrates them to can()). Thin wrapper:
   * same rules as can(), unknown slugs are refused.
   */
  const hasPermission = (permission: string): boolean => isPermissionSlug(permission) && can(permission);

  /**
   * Merge a permission payload (GET /auth/permissions, or the matching
   * fields of /auth/me) into the stored user. Returns true when anything
   * that affects access changed. A payload with `permissions` but without
   * `permissionScopes` drops the stored scopes, so the two never disagree.
   */
  function applyPermissions(raw: unknown): boolean {
    if (!user.value || !raw || typeof raw !== 'object') return false;
    const p = raw as Record<string, unknown>;
    const before = user.value;
    const next: User = { ...before };
    if (Array.isArray(p.permissions)) {
      next.permissions = p.permissions.filter((s): s is string => typeof s === 'string');
      next.permissionScopes = sanitizeScopes(p.permissionScopes);
    } else if (p.permissionScopes !== undefined) {
      next.permissionScopes = sanitizeScopes(p.permissionScopes);
    }
    if ('permissionsVersion' in p) next.permissionsVersion = typeof p.permissionsVersion === 'string' ? p.permissionsVersion : null;
    if (typeof p.fullAccess === 'boolean') next.fullAccess = p.fullAccess;
    if (typeof p.isOwner === 'boolean') next.isOwner = p.isOwner;
    if (Array.isArray(p.enabledModules)) next.enabledModules = p.enabledModules.filter((s): s is string => typeof s === 'string');
    if (typeof p.role === 'string' && p.role) next.role = p.role;
    if ('roleId' in p) next.roleId = typeof p.roleId === 'number' ? p.roleId : null;
    if ('roleName' in p) next.roleName = typeof p.roleName === 'string' ? p.roleName : null;
    const changed = PERMISSION_FIELDS.some((f) => stableKey(next[f]) !== stableKey(before[f]));
    user.value = next;
    return changed;
  }

  let permissionsInflight: Promise<boolean> | null = null;
  /** GET /auth/permissions answered 404 (backend not deployed yet): use /auth/me until sign-out. */
  let permissionsEndpointMissing = false;

  /**
   * Re-read the signed-in staff member's permissions and merge them into the
   * store (UI updates reactively, no reload). Single flight. Falls back to
   * /auth/me while /auth/permissions 404s. Keeps the cached permissions when
   * the request fails (offline). An answer for a user who has since signed
   * out or switched account is dropped. Resolves to "something changed".
   */
  async function refreshPermissions(_opts: { reason?: string } = {}): Promise<boolean> {
    if (principal.value !== 'staff' || !accessToken.value || !user.value) return false;
    if (permissionsInflight) return permissionsInflight;
    const forUserId = user.value.id;
    permissionsInflight = (async () => {
      try {
        let payload: unknown = null;
        if (!permissionsEndpointMissing) {
          try {
            payload = (await api.get('/auth/permissions'))?.data?.data ?? null;
          } catch (err) {
            if (httpStatus(err) !== 404) throw err;
            permissionsEndpointMissing = true;
          }
        }
        if (!payload) payload = (await api.get('/auth/me'))?.data?.data ?? null;
        if (!user.value || user.value.id !== forUserId || principal.value !== 'staff') return false;
        lastPermissionsCheckAt.value = Date.now();
        return applyPermissions(payload);
      } catch {
        return false;
      } finally {
        permissionsInflight = null;
      }
    })();
    return permissionsInflight;
  }

  /**
   * Merge a profile response (GET/PATCH /auth/profile) into the stored user —
   * whitelisted profile fields only (R9 / F22): permissions, scopes, version,
   * role and owner flag are kept as they are.
   */
  function applyProfile(data: Partial<User> | null | undefined): void {
    if (!user.value || !data || typeof data !== 'object') return;
    const src = data as Record<string, unknown>;
    const next: User = { ...user.value };
    const target = next as unknown as Record<string, unknown>;
    for (const key of PROFILE_FIELDS) {
      if (src[key] !== undefined) target[key] = src[key];
    }
    if (data.company && typeof data.company === 'object' && next.company) {
      const company = { ...next.company } as Record<string, unknown>;
      for (const key of COMPANY_DISPLAY_FIELDS) {
        const value = (data.company as Record<string, unknown>)[key];
        if (value !== undefined) company[key] = value;
      }
      next.company = company as unknown as User['company'];
    }
    user.value = next;
  }

  /** Forget the session in this tab (state + storage). No API call. */
  function clearSession(): void {
    user.value = null;
    accessToken.value = null;
    refreshToken.value = null;
    userType.value = null;
    lastPermissionsCheckAt.value = null;
    permissionsEndpointMissing = false;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userType');
    // Note: tenantSubdomain is intentionally kept so people return to their company login
  }

  /**
   * Sign out without calling the API — the server already ended the session
   * (socket `session:revoked`, refresh token rejected).
   */
  function signOutLocally(): void {
    deregisterPushNotifications().catch(() => {});
    clearSession();
  }

  async function login(credentials: LoginCredentials): Promise<LoginResult> {
    loading.value = true;
    try {
      const response = await api.post('/auth/login', credentials);
      const data = response.data.data;

      // Check if 2FA is required
      if (data.twoFactorRequired) {
        return {
          success: false,
          twoFactorRequired: true,
          userId: data.userId,
        };
      }

      const { user: userData, accessToken: token, refreshToken: refresh } = data;

      user.value = userData;
      accessToken.value = token;
      refreshToken.value = refresh;
      userType.value = 'user';
      // Confirmed only when the payload carries them; otherwise the router refreshes first.
      lastPermissionsCheckAt.value = Array.isArray(userData.permissions) ? Date.now() : null;
      permissionsEndpointMissing = false;

      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refresh);
      localStorage.setItem('userType', 'user');

      // Store tenant subdomain for API requests
      if (userData.company?.subdomain) {
        localStorage.setItem('tenantSubdomain', userData.company.subdomain);
      }

      // Register device for push notifications (Capacitor only, non-blocking)
      initPushNotifications().catch(() => {});

      return { success: true };
    } finally {
      loading.value = false;
    }
  }

  async function loginSuperAdmin(credentials: { email: string; password: string; turnstileToken?: string }): Promise<void> {
    loading.value = true;
    try {
      const response = await api.post('/superadmin/login', credentials);
      const { user: userData, accessToken: token, refreshToken: refresh } = response.data.data;

      user.value = userData;
      accessToken.value = token;
      refreshToken.value = refresh;
      userType.value = 'superadmin';

      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refresh);
      localStorage.setItem('userType', 'superadmin');
      // Clear tenant subdomain for super admin (they're not part of any company)
      localStorage.removeItem('tenantSubdomain');
    } finally {
      loading.value = false;
    }
  }

  async function loginClient(credentials: LoginCredentials): Promise<LoginResult> {
    loading.value = true;
    try {
      const response = await api.post('/auth/client/login', credentials);
      const { user: userData, accessToken: token, refreshToken: refresh } = response.data.data;

      user.value = userData;
      accessToken.value = token;
      refreshToken.value = refresh;
      userType.value = 'client';

      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refresh);
      localStorage.setItem('userType', 'client');

      if (userData.company?.subdomain) {
        localStorage.setItem('tenantSubdomain', userData.company.subdomain);
      }

      // Register device for push notifications (Capacitor only, non-blocking)
      initPushNotifications().catch(() => {});

      return { success: true };
    } finally {
      loading.value = false;
    }
  }

  async function register(data: RegisterData): Promise<{ emailVerified: boolean }> {
    loading.value = true;
    try {
      const response = await api.post('/auth/register', data);
      const { user: userData, accessToken: token, refreshToken: refresh, emailVerified, billingSetupCompleted } = response.data.data;

      user.value = { ...userData, emailVerified: emailVerified ?? false, billingSetupCompleted: billingSetupCompleted ?? false };
      accessToken.value = token;
      refreshToken.value = refresh;
      userType.value = 'user';
      // The register response carries no permissions: the router fetches them before the first page.
      lastPermissionsCheckAt.value = Array.isArray(userData.permissions) ? Date.now() : null;
      permissionsEndpointMissing = false;

      localStorage.setItem('accessToken', token);
      localStorage.setItem('refreshToken', refresh);
      localStorage.setItem('userType', 'user');

      // Store tenant subdomain for API requests
      if (userData.company?.subdomain) {
        localStorage.setItem('tenantSubdomain', userData.company.subdomain);
      }

      return { emailVerified: emailVerified ?? false };
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    // Remove push notification listeners and clear notification center
    deregisterPushNotifications().catch(() => {});

    try {
      // The refresh token lets the server end this session (it accepts an
      // expired access token for that).
      await api.post('/auth/logout', refreshToken.value ? { refreshToken: refreshToken.value } : {});
    } catch {
      // Ignore logout errors
    } finally {
      clearSession();
    }
  }

  async function checkAuth(): Promise<void> {
    if (isInitialized.value) return;

    if (!accessToken.value) {
      isInitialized.value = true;
      return;
    }

    try {
      // Check userType to determine which endpoint to call first
      if (userType.value === 'superadmin') {
        // Super admin - call superadmin/me endpoint
        const superAdminResponse = await api.get('/superadmin/me');
        user.value = superAdminResponse.data.data;
      } else if (userType.value === 'client') {
        // Client - call client/me endpoint
        const clientResponse = await api.get('/auth/client/me');
        user.value = clientResponse.data.data;
        if (clientResponse.data.data.company?.subdomain) {
          localStorage.setItem('tenantSubdomain', clientResponse.data.data.company.subdomain);
        }
      } else {
        // Regular user - call auth/me endpoint
        const response = await api.get('/auth/me');
        user.value = response.data.data;
        userType.value = 'user';
        lastPermissionsCheckAt.value = Date.now();
        localStorage.setItem('userType', 'user');
        // Restore subdomain for API requests
        if (response.data.data.company?.subdomain) {
          localStorage.setItem('tenantSubdomain', response.data.data.company.subdomain);
        }
      }
    } catch (primaryErr) {
      // OFFLINE (or the server failing: 5xx, 429, a refresh that failed that
      // way): we have a token + a cached session → keep the user signed in
      // instead of kicking them to /signin. The SPA shell is precached by
      // Workbox, so this makes the PWA usable (read-only) without a connection.
      if (isTransientError(primaryErr) && accessToken.value) {
        const cached = readPersistedUser();
        if (cached) {
          user.value = cached;
          isInitialized.value = true;
          return;
        }
      }

      // If the preferred endpoint fails (auth error), try the other one
      try {
        if (userType.value === 'superadmin') {
          // Super admin endpoint failed, try regular user (unlikely but handle it)
          const response = await api.get('/auth/me');
          user.value = response.data.data;
          userType.value = 'user';
          lastPermissionsCheckAt.value = Date.now();
          localStorage.setItem('userType', 'user');
          if (response.data.data.company?.subdomain) {
            localStorage.setItem('tenantSubdomain', response.data.data.company.subdomain);
          }
        } else {
          // Regular user failed, try super admin
          const superAdminResponse = await api.get('/superadmin/me');
          user.value = superAdminResponse.data.data;
          userType.value = 'superadmin';
          localStorage.setItem('userType', 'superadmin');
          localStorage.removeItem('tenantSubdomain');
        }
      } catch (secondaryErr) {
        // Same offline guard for the fallback endpoint
        if (isTransientError(secondaryErr) && accessToken.value) {
          const cached = readPersistedUser();
          if (cached) {
            user.value = cached;
            isInitialized.value = true;
            return;
          }
        }
        // Genuine auth failure (server replied 401) — try to refresh,
        // which logs out if the refresh token is also dead.
        await refreshAccessToken();
      }
    } finally {
      isInitialized.value = true;
      // Re-register push notifications when session is restored on app open.
      // Skip when offline — it needs the network and would just throw.
      if (user.value && (typeof navigator === 'undefined' || navigator.onLine)) {
        initPushNotifications().catch(() => {});
      }
    }
  }

  async function refreshAccessToken(): Promise<boolean> {
    if (!refreshToken.value) {
      await logout();
      return false;
    }

    try {
      // Shared with the 401 interceptor (single flight). It stores the new
      // tokens in localStorage and, through TOKENS_REFRESHED_EVENT, here.
      const token = await refreshSession();
      accessToken.value = token;
      refreshToken.value = localStorage.getItem('refreshToken');

      // Retry fetching user based on type
      if (userType.value === 'superadmin') {
        const superAdminResponse = await api.get('/superadmin/me');
        user.value = superAdminResponse.data.data;
      } else if (userType.value === 'client') {
        const clientResponse = await api.get('/auth/client/me');
        user.value = clientResponse.data.data;
        if (clientResponse.data.data.company?.subdomain) {
          localStorage.setItem('tenantSubdomain', clientResponse.data.data.company.subdomain);
        }
      } else {
        const userResponse = await api.get('/auth/me');
        user.value = userResponse.data.data;
        lastPermissionsCheckAt.value = Date.now();

        // Restore subdomain for API requests
        if (userResponse.data.data.company?.subdomain) {
          localStorage.setItem('tenantSubdomain', userResponse.data.data.company.subdomain);
        }
      }

      return true;
    } catch (err) {
      // The server refused the session (refreshSession has signed out).
      if (isDefinitiveRefreshRejection(err)) {
        clearSession();
        return false;
      }
      // Offline / timeout / 5xx — don't destroy the session. Restore the
      // cached user so the PWA stays usable; the tokens stay for the retry.
      if (isTransientError(err) && accessToken.value) {
        const cached = readPersistedUser();
        if (cached) {
          user.value = cached;
          return true;
        }
        return false;
      }
      await logout();
      return false;
    }
  }

  async function forgotPassword(email: string): Promise<void> {
    await api.post('/auth/forgot-password', { email });
  }

  async function resetPassword(token: string, password: string): Promise<void> {
    await api.post('/auth/reset-password', { token, password });
  }

  async function updateProfile(data: Partial<User>): Promise<{ data: any; message?: string }> {
    const response = await api.patch('/auth/profile', data);
    applyProfile(response.data.data);
    return response.data;
  }

  async function updatePassword(currentPassword: string, newPassword: string): Promise<void> {
    // With this session's refresh token the server keeps it and signs every
    // other device out (without it, this tab was signed out at its next refresh).
    await api.patch('/auth/password', { currentPassword, newPassword, ...(refreshToken.value ? { refreshToken: refreshToken.value } : {}) });
  }

  async function fetchProfile(): Promise<User> {
    const response = await api.get('/auth/profile');
    applyProfile(response.data.data);
    return response.data.data;
  }

  async function uploadAvatar(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await api.post('/auth/avatar', formData);
    if (user.value) {
      user.value.avatar = response.data.data.avatar;
    }
    return response.data.data.avatar;
  }

  return {
    user,
    accessToken,
    refreshToken,
    userType,
    isInitialized,
    loading,
    isAuthenticated,
    fullName,
    avatarUrl,
    defaultRoute,
    // permissions
    principal,
    permissionSnapshot,
    hasLegacyPermissions,
    isFullAccess,
    permissionsVersion,
    lastPermissionsCheckAt,
    can,
    canAny,
    canAll,
    scopeOf,
    meets,
    canOnRecord,
    hasPermission,
    refreshPermissions,
    applyPermissions,
    applyProfile,
    signOutLocally,
    login,
    loginClient,
    loginSuperAdmin,
    register,
    logout,
    checkAuth,
    refreshAccessToken,
    forgotPassword,
    resetPassword,
    updateProfile,
    updatePassword,
    fetchProfile,
    uploadAvatar,
  };
});
